import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useQueueAnnouncements, useSoundArm, ACCEPT_RECEPTION, playAnnouncement } from "@/lib/announcer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardList, UserPlus, Search, RotateCcw, X, CheckCircle2,
  Play, Clock, Activity, History as HistoryIcon, ChevronDown, ChevronUp,
  Receipt, FilePlus2, Stethoscope, BellRing, Volume2, CalendarClock, Monitor, QrCode, Copy, Printer, Inbox, Download, Pencil,
} from "lucide-react";
import VitalsForm, { EMPTY_VITALS, vitalsSummary, hasAnyVital } from "@/components/VitalsForm";
import ReservationsTodayPanel from "@/components/ReservationsTodayPanel";
import CallOutVoicePicker from "@/components/CallOutVoicePicker";

const STATUS_LABEL = {
  reserved: "Reserved",
  waiting: "Waiting",
  in_progress: "In Progress",
  billing: "Billing / Cashier",
  paid: "Paid",
  done: "Done",
  cancelled: "Cancelled",
};
const STATUS_STYLE = {
  reserved: "bg-red-50 text-red-700 border-red-200",
  waiting: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  billing: "bg-purple-50 text-purple-700 border-purple-200",
  paid: "bg-green-100 text-green-800 border-green-300 font-semibold",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

// Records imported from other systems store gender in mixed case ("MALE",
// "female", "M"). Map to the edit form's canonical options so the dialog
// reflects what is on file instead of showing blank — a blank that would
// otherwise overwrite (wipe) the gender on the next save.
function canonGender(g) {
  const s = String(g || "").trim().toLowerCase();
  if (s === "m" || s === "male") return "Male";
  if (s === "f" || s === "female") return "Female";
  if (s === "o" || s === "other") return "Other";
  return String(g || "").trim();
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function Reception() {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Selection / form
  const [mode, setMode] = useState("existing");
  const [patientQuery, setPatientQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [assignedDoctorId, setAssignedDoctorId] = useState("");
  const [reason, setReason] = useState("");
  const [showVitalsForm, setShowVitalsForm] = useState(false);
  const [newVitals, setNewVitals] = useState({ ...EMPTY_VITALS });

  // New patient form
  const [npLast, setNpLast] = useState("");
  const [npFirst, setNpFirst] = useState("");
  const [npMiddle, setNpMiddle] = useState("");
  const [npBirthday, setNpBirthday] = useState("");
  const [npAge, setNpAge] = useState("");
  const [npGender, setNpGender] = useState("");
  const [npContact, setNpContact] = useState("");
  const [npAddress, setNpAddress] = useState("");

  // Vitals edit dialog
  const [vitalsDialog, setVitalsDialog] = useState({ open: false, queue: null, value: { ...EMPTY_VITALS }, saving: false });
  // History dialog
  const [historyDialog, setHistoryDialog] = useState({ open: false, patient: null, items: [], loading: false });
  const [showBookingQr, setShowBookingQr] = useState(false);
  const [notes, setNotes] = useState([]);
  const [popupNote, setPopupNote] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [editPatient, setEditPatient] = useState({ open: false, saving: false, pid: "", queueId: "", form: null });
  const { user } = useAuth();

  // Hear every call-out at the front desk: our own, the cashier's, and the doctor's.
  const { armed: soundOn, arm: armSound } = useSoundArm();
  const { markSelfPlayed } = useQueueAnnouncements({ accept: ACCEPT_RECEPTION, enabled: soundOn });
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [callingId, setCallingId] = useState("");

  const openEditPatient = async (q) => {
    const pid = q.patient_id;
    if (!pid) { toast.error("No linked patient to edit"); return; }
    let p = q.patient_snapshot || {};
    try { const r = await api.get(`/patients/${pid}`); if (r.data) p = r.data; } catch {}
    // Split a legacy full name into first / middle / last for editing.
    let first = p.first_name || "", middle = p.middle_name || "", last = p.last_name || "";
    if (!first && !middle && !last) {
      const parts = String(p.full_name || "").trim().split(/\s+/);
      if (parts.length === 1) first = parts[0] || "";
      else if (parts.length === 2) { first = parts[0]; last = parts[1]; }
      else { first = parts.slice(0, -2).join(" "); middle = parts[parts.length - 2]; last = parts[parts.length - 1]; }
    }
    setEditPatient({
      open: true, saving: false, pid, queueId: q.id,
      form: {
        first_name: first, middle_name: middle, last_name: last,
        gender: canonGender(p.gender), birthday: (p.birthday || "").slice(0, 10),
        contact_number: p.contact_number || "", address: p.address || "",
      },
    });
  };

  const saveEditPatient = async () => {
    const f = editPatient.form || {};
    if (!f.first_name?.trim() || !f.last_name?.trim()) { toast.error("First name and surname are required"); return; }
    setEditPatient((s) => ({ ...s, saving: true }));
    try {
      await api.put(`/patients/${editPatient.pid}`, {
        first_name: f.first_name.trim(),
        middle_name: (f.middle_name || "").trim(),
        last_name: f.last_name.trim(),
        gender: f.gender || "",
        birthday: f.birthday || "",
        contact_number: (f.contact_number || "").trim(),
        address: (f.address || "").trim(),
      });
      toast.success("Patient details updated");
      setEditPatient({ open: false, saving: false, pid: "", queueId: "", form: null });
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
      setEditPatient((s) => ({ ...s, saving: false }));
    }
  };
  const hospitalName = user?.active_hospital_name || user?.hospital_name || "Clinic";
  const bookingUrl = `${window.location.origin}/book/${user?.active_hospital_id || ""}`;
  const qrRef = useRef(null);
  // Compose a printable flyer: logo (if any) + "Scan to Reserve Appointment Online" + QR + link.
  const composeQr = () => new Promise((resolve) => {
    const qrCanvas = qrRef.current?.querySelector("canvas");
    if (!qrCanvas) return resolve("");
    const title = "Scan to Reserve Appointment Online";
    const W = 480, pad = 32, qrSize = 300;
    const render = (logoImg) => {
      const meas = document.createElement("canvas").getContext("2d");
      let logoH = 0, logoW = 0;
      if (logoImg && logoImg.width) {
        logoH = 84;
        logoW = Math.min(W - 2 * pad, (logoImg.width / logoImg.height) * logoH);
      }
      let fs = 22;
      meas.font = `600 ${fs}px system-ui, sans-serif`;
      while (meas.measureText(title).width > W - 2 * pad && fs > 12) { fs -= 1; meas.font = `600 ${fs}px system-ui, sans-serif`; }
      const H = pad + (logoImg ? logoH + 18 : 0) + fs + 12 + 22 + qrSize + 28 + pad;
      const c = document.createElement("canvas");
      c.width = W; c.height = H;
      const g = c.getContext("2d");
      g.fillStyle = "#ffffff"; g.fillRect(0, 0, W, H);
      g.textAlign = "center";
      let y = pad;
      if (logoImg && logoW) { g.drawImage(logoImg, (W - logoW) / 2, y, logoW, logoH); y += logoH + 18; }
      g.fillStyle = "#0f172a"; g.font = `600 ${fs}px system-ui, sans-serif`;
      g.fillText(title, W / 2, y + fs); y += fs + 12;
      g.fillStyle = "#475569"; g.font = "500 15px system-ui, sans-serif";
      g.fillText(hospitalName, W / 2, y + 14); y += 22;
      g.drawImage(qrCanvas, (W - qrSize) / 2, y, qrSize, qrSize); y += qrSize + 20;
      g.fillStyle = "#94a3b8"; g.font = "12px system-ui, sans-serif";
      g.fillText(bookingUrl.length > 56 ? bookingUrl.slice(0, 56) + "…" : bookingUrl, W / 2, y);
      try { resolve(c.toDataURL("image/png")); } catch { resolve(qrCanvas.toDataURL("image/png")); }
    };
    const logoUrl = user?.branding?.logo_url;
    if (logoUrl) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => render(img);
      img.onerror = () => render(null);
      img.src = logoUrl;
    } else {
      render(null);
    }
  });
  const downloadQr = async () => {
    const url = await composeQr();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `booking-qr-${(hospitalName || "clinic").replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
  };
  const printQr = async () => {
    const url = await composeQr();
    if (!url) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(
      `<html><head><title>Booking QR — ${hospitalName}</title></head>` +
      `<body style="margin:0;text-align:center;padding:32px"><img src="${url}" style="max-width:420px;width:100%"/></body></html>`
    );
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const openQueueScreen = () => {
    window.open("/queue-display", "emedbay_queue_screen", "popup=yes,width=1400,height=900");
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      // Independent calls — a failure of one list must NOT wipe the others.
      const [dRes, pRes, qRes] = await Promise.allSettled([
        api.get("/doctors"),
        api.get("/patients"),
        api.get("/queue/today"),
      ]);

      if (dRes.status === "fulfilled") setDoctors(dRes.value.data || []);
      else toast.error(formatErr(dRes.reason));

      if (pRes.status === "fulfilled") setPatients(pRes.value.data || []);
      else toast.error(formatErr(pRes.reason));

      if (qRes.status === "fulfilled") {
        const qd = qRes.value.data;
        setQueue(Array.isArray(qd) ? qd : (qd?.items || []));
      } else {
        toast.error(formatErr(qRes.reason));
      }

      if (!assignedDoctorId && dRes.status === "fulfilled" && (dRes.value.data || []).length) {
        setAssignedDoctorId(dRes.value.data[0].id);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNotes = async () => {
    try {
      const { data } = await api.get("/reception-notes");
      const list = Array.isArray(data) ? data : [];
      setNotes(list);
      setPopupNote((cur) => cur || list.find((n) => n.status !== "acknowledged") || null);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadNotes();
    const t = setInterval(loadNotes, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ackNote = async (nid) => {
    try { await api.post(`/reception-notes/${nid}/acknowledge`); } catch (e) { toast.error(formatErr(e)); }
    setNotes((prev) => {
      const upd = prev.map((n) => (n.id === nid ? { ...n, status: "acknowledged", acknowledged_at: new Date().toISOString() } : n));
      setPopupNote(upd.find((n) => n.status !== "acknowledged") || null);
      return upd;
    });
  };
  const unreadCount = notes.filter((n) => n.status !== "acknowledged").length;

  const filteredPatients = useMemo(() => {
    const q = patientQuery.trim().toLowerCase();
    if (!q) return patients.slice(0, 8);
    return patients
      .filter((p) =>
        (p.full_name || "").toLowerCase().includes(q) ||
        String(p.contact_number || "").toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [patientQuery, patients]);

  const resetForm = () => {
    setSelectedPatient(null);
    setPatientQuery("");
    setReason("");
    setNpLast(""); setNpFirst(""); setNpMiddle(""); setNpBirthday("");
    setNpAge(""); setNpGender(""); setNpContact(""); setNpAddress("");
    setMode("existing");
    setNewVitals({ ...EMPTY_VITALS });
    setShowVitalsForm(false);
  };

  const createPatientThen = async () => {
    if (!npLast.trim() || !npFirst.trim()) {
      toast.error("Surname and first name are required");
      return null;
    }
    const payload = {
      first_name: npFirst.trim(),
      middle_name: npMiddle.trim(),
      last_name: npLast.trim(),
      birthday: npBirthday || "",
      // Birthday wins when present (the backend derives age from it); age is a
      // fallback for walk-ins whose exact birthday isn't known.
      age: npAge ? Number(npAge) : null,
      gender: npGender || "",
      contact_number: npContact || "",
      address: npAddress || "",
    };
    try {
      const { data } = await api.post("/reception/patients", payload);
      toast.success("Patient created");
      setPatients((prev) => [data, ...prev]);
      return data;
    } catch (e) {
      toast.error(formatErr(e));
      return null;
    }
  };

  const onAddToQueue = async () => {
    if (!assignedDoctorId) { toast.error("Select a doctor"); return; }
    setBusy(true);
    try {
      let patient = selectedPatient;
      if (mode === "new") {
        patient = await createPatientThen();
        if (!patient) { setBusy(false); return; }
      }
      if (!patient?.id) { toast.error("Select or create a patient"); setBusy(false); return; }
      const { data: q } = await api.post("/queue", {
        patient_id: patient.id,
        assigned_doctor_id: assignedDoctorId,
        reason: reason.trim(),
      });
      // If reception entered vitals, save them
      if (hasAnyVital(newVitals)) {
        try {
          await api.put(`/queue/${q.id}/vitals`, newVitals);
        } catch (e) {
          toast.error("Queued, but failed to save vitals: " + formatErr(e));
        }
      }
      toast.success("Added to today's queue");
      resetForm();
      await loadAll();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const announcePatient = async (q) => {
    // TTS generation takes a second or two before anything is audible, which is
    // exactly when an impatient second click lands — creating a second, real
    // announcement that every station then plays on top of the first.
    if (callingId) return;
    setCallingId(q.id);
    try {
      // A patient already at the cashier lane is called to the cashier; everyone
      // else the reception desk calls is sent to the reception.
      const callType = q.status === "billing" ? "cashier" : "reception";
      const { data } = await api.post(`/queue/${q.id}/announce`, {
        call_type: callType,
      });

      // Calling only announces — the patient stays in their lane. Reflect any
      // server-side call metadata without changing the stage.
      setQueue((prev) => prev.map((x) => (
        x.id === q.id ? { ...x, ...(data.queue || {}) } : x
      )));

      // Play it here immediately (serialized by the announcer, single play);
      // the poller skips announcements this user created, so it can't overlap.
      // Repeat once after ~2.5 seconds so patients don't miss the call.
      markSelfPlayed(data.id);
      playAnnouncement(data, { repeat: 2 });

      toast.success("Patient called");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setCallingId("");
    }
  };

  const updateStatus = async (qid, status) => {
    try {
      await api.put(`/queue/${qid}/status`, { status });
      setQueue((prev) => prev.map((x) => (x.id === qid ? { ...x, status } : x)));
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const reassign = async (qid, doctorId) => {
    if (!doctorId) return;
    try {
      const { data } = await api.put(`/queue/${qid}/assign-doctor`, { assigned_doctor_id: doctorId });
      setQueue((prev) => prev.map((x) => (x.id === qid ? data : x)));
      toast.success("Doctor reassigned");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // Vitals dialog
  const openVitalsDialog = (q) => {
    setVitalsDialog({
      open: true,
      queue: q,
      value: { ...EMPTY_VITALS, ...(q.vital_signs || {}) },
      saving: false,
    });
  };
  const saveVitals = async () => {
    const { queue: q, value } = vitalsDialog;
    if (!q) return;
    setVitalsDialog((s) => ({ ...s, saving: true }));
    try {
      const { data } = await api.put(`/queue/${q.id}/vitals`, value);
      setQueue((prev) => prev.map((x) => (x.id === q.id ? data : x)));
      toast.success("Vitals saved");
      setVitalsDialog({ open: false, queue: null, value: { ...EMPTY_VITALS }, saving: false });
    } catch (e) {
      toast.error(formatErr(e));
      setVitalsDialog((s) => ({ ...s, saving: false }));
    }
  };

  // History dialog
  const openHistoryDialog = async (q) => {
    setHistoryDialog({ open: true, patient: { id: q.patient_id, name: q.patient_snapshot?.full_name }, items: [], loading: true });
    try {
      const { data } = await api.get(`/patients/${q.patient_id}/vitals`);
      setHistoryDialog((s) => ({ ...s, items: data || [], loading: false }));
    } catch (e) {
      toast.error(formatErr(e));
      setHistoryDialog((s) => ({ ...s, loading: false }));
    }
  };

  // Queue → POS / RX / Chart shortcuts
  const createPosFromQueue = async (q) => {
    try {
      const { data } = await api.post(`/pos/from-queue/${q.id}`);
      toast.success(`POS ${data.transaction_number} ready`);
      navigate(`/pos/transactions/${data.id}`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };
  const createRxFromQueue = (q) => {
    // NewPrescription reads ?patient (not patient_id); include returnTo so Back lands here.
    navigate(`/prescriptions/new?patient=${q.patient_id}&returnTo=${encodeURIComponent("/reception")}`);
  };
  const openChartFromQueue = (q) => {
    navigate(`/patients/${q.patient_id}`);
  };

  const isReservedRow = (q) =>
    q?.source === "reservation" || q?.priority === "reserved" || String(q?.id || "").startsWith("reservation-") ||
    // Follow-ups materialize as reserved until checked in; once waiting they
    // behave like any other queue row.
    (q?.source === "follow_up" && q?.status === "reserved");

  const patientNameForQueue = (q) => q?.patient_snapshot?.full_name || q?.patient_name || "—";

  const patientMetaForQueue = (q) => {
    if (q?.patient_snapshot) {
      return [
        q.patient_snapshot.age ? `${q.patient_snapshot.age} y/o` : "",
        q.patient_snapshot.gender || "",
      ].filter(Boolean).join(" ");
    }
    return q?.patient_contact || "";
  };

  const timeForQueue = (q) => {
    if (isReservedRow(q)) return q.reservation_time || fmtTime(q.reservation_at);
    return fmtTime(q.created_at);
  };

  const arriveReservationFromQueue = async (q) => {
    if (!q?.reservation_id) return;
    try {
      await api.post(`/reservations/${q.reservation_id}/arrive`);
      toast.success("Reserved patient checked in");
      await loadAll();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const counts = useMemo(() => {
    const c = { reserved: 0, waiting: 0, in_progress: 0, billing: 0, paid: 0, done: 0, cancelled: 0 };
    queue.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    return c;
  }, [queue]);

  return (
    <div className="p-6 space-y-6">
      <ReservationsTodayPanel />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ClipboardList className="text-blue-600" /> Reception Queue
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage today's patients, record vitals, and assign them to a doctor.</p>
        </div>
        <Button variant="outline" onClick={() => setInboxOpen(true)} data-testid="btn-inbox" className="relative">
          <Inbox size={16} className="mr-2" />
          Inbox
          {unreadCount > 0 ? (
            <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unreadCount}</span>
          ) : null}
        </Button>
        <Button variant="outline" onClick={() => setShowBookingQr(true)} data-testid="btn-booking-qr">
          <QrCode size={16} className="mr-2" />
          Booking QR
        </Button>
        <Button variant="outline" onClick={openQueueScreen} data-testid="btn-open-queue-screen">
          <Monitor size={16} className="mr-2" />
          Queue Display
        </Button>
        <Button
          variant={soundOn ? "outline" : "default"}
          onClick={armSound}
          disabled={soundOn}
          className={soundOn ? "" : "bg-amber-600 hover:bg-amber-700 text-white"}
          data-testid="btn-enable-sound"
          title="Play call-outs on this computer (doctor + cashier calls too)"
        >
          <BellRing size={16} className="mr-2" />
          {soundOn ? "Sound On" : "Enable Sound"}
        </Button>
        <Button
          variant="outline"
          onClick={() => setVoiceOpen(true)}
          data-testid="btn-callout-voice"
          title="Choose which voice announces patients on this computer"
        >
          <Volume2 size={16} className="mr-2" />
          Voice
        </Button>
        <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="btn-refresh-queue">
          <RotateCcw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        {["reserved", "waiting", "in_progress", "billing", "paid", "done", "cancelled"].map((s) => (
          <Card key={s} className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">{STATUS_LABEL[s]}</div>
            <div className="text-2xl font-semibold text-slate-900 mt-1">{counts[s] || 0}</div>
          </Card>
        ))}
      </div>

      {/* Add to queue */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Add Patient to Queue</h2>
          <div className="flex bg-slate-100 rounded-md p-1 text-sm">
            <button
              type="button"
              onClick={() => setMode("existing")}
              className={`px-3 py-1 rounded ${mode === "existing" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}
              data-testid="btn-mode-existing"
            >
              <Search size={14} className="inline mr-1" /> Existing
            </button>
            <button
              type="button"
              onClick={() => setMode("new")}
              className={`px-3 py-1 rounded ${mode === "new" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}
              data-testid="btn-mode-new"
            >
              <UserPlus size={14} className="inline mr-1" /> New patient
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {mode === "existing" ? (
            <div className="space-y-2">
              <Label>Search patient</Label>
              <Input
                value={patientQuery}
                onChange={(e) => { setPatientQuery(e.target.value); setSelectedPatient(null); }}
                placeholder="Name or contact number"
                data-testid="input-patient-search"
              />
              {patientQuery && !selectedPatient && (
                <div className="border rounded-md bg-white max-h-56 overflow-auto divide-y">
                  {filteredPatients.length === 0 && (
                    <div className="p-3 text-sm text-slate-500">No patients found</div>
                  )}
                  {filteredPatients.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPatient(p); setPatientQuery(p.full_name); }}
                      className="w-full text-left p-2 hover:bg-slate-50"
                      data-testid={`opt-patient-${p.id}`}
                    >
                      <div className="text-sm font-medium text-slate-900">{p.full_name}</div>
                      <div className="text-xs text-slate-500">
                        {p.age ? `${p.age} y/o` : ""} {p.gender || ""} {p.contact_number ? `• ${p.contact_number}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selectedPatient && (
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm flex items-center justify-between">
                  <div>
                    <div className="font-medium text-blue-900">{selectedPatient.full_name}</div>
                    <div className="text-xs text-blue-700">
                      {selectedPatient.age ? `${selectedPatient.age} y/o` : ""} {selectedPatient.gender || ""}
                      {selectedPatient.contact_number ? ` • ${selectedPatient.contact_number}` : ""}
                    </div>
                  </div>
                  <button onClick={() => { setSelectedPatient(null); setPatientQuery(""); }} className="text-blue-700">
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Surname *</Label>
                  <Input value={npLast} onChange={(e) => setNpLast(e.target.value)} data-testid="np-last" />
                </div>
                <div>
                  <Label>First name *</Label>
                  <Input value={npFirst} onChange={(e) => setNpFirst(e.target.value)} data-testid="np-first" />
                </div>
                <div>
                  <Label>Middle name</Label>
                  <Input value={npMiddle} onChange={(e) => setNpMiddle(e.target.value)} placeholder="Optional" data-testid="np-middle" />
                </div>
                <div>
                  <Label>Birthday</Label>
                  <Input type="date" value={npBirthday} onChange={(e) => setNpBirthday(e.target.value)} data-testid="np-birthday" />
                </div>
                <div>
                  <Label>Age</Label>
                  <Input type="number" value={npAge} onChange={(e) => setNpAge(e.target.value)} data-testid="np-age" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <select
                    className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm"
                    value={npGender}
                    onChange={(e) => setNpGender(e.target.value)}
                    data-testid="np-gender"
                  >
                    <option value="">—</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Contact</Label>
                  <Input value={npContact} onChange={(e) => setNpContact(e.target.value)} data-testid="np-contact" />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Input value={npAddress} onChange={(e) => setNpAddress(e.target.value)} data-testid="np-address" />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label>Assign doctor *</Label>
              <select
                className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm"
                value={assignedDoctorId}
                onChange={(e) => setAssignedDoctorId(e.target.value)}
                data-testid="select-doctor"
              >
                {doctors.length === 0 && <option value="">No doctors found</option>}
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} {d.role === "admin" ? "(Admin)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Visit reason</Label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Follow-up, fever, headache..."
                data-testid="input-reason"
              />
            </div>
          </div>
        </div>

        {/* Vitals collapsible */}
        <div className="mt-4 border-t pt-4">
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-700"
            onClick={() => setShowVitalsForm((v) => !v)}
            data-testid="btn-toggle-vitals-form"
          >
            <Activity size={14} className="text-emerald-600" />
            Vital Signs <span className="text-slate-400 font-normal">(optional)</span>
            {showVitalsForm ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showVitalsForm && (
            <div className="mt-3" data-testid="vitals-add-form">
              <VitalsForm value={newVitals} onChange={setNewVitals} idPrefix="add-vitals" />
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-4">
          <Button onClick={onAddToQueue} disabled={busy} data-testid="btn-add-queue">
            <ClipboardList size={14} className="mr-2" /> Add to Today's Queue
          </Button>
          <Button variant="outline" onClick={resetForm} disabled={busy}>Reset</Button>
        </div>
      </Card>

      {/* Today queue table */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Today's Queue</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Reserved patients appear first when their reservation window opens.
            </div>
          </div>
          <div className="text-xs text-slate-500">{queue.length} entries</div>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Time</th>
                <th className="text-left px-4 py-2 font-medium">Patient</th>
                <th className="text-left px-4 py-2 font-medium">Doctor</th>
                <th className="text-left px-4 py-2 font-medium">Vitals</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {queue.length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-500 py-8">No queue entries for today yet.</td></tr>
              )}

              {queue.map((q) => {
                const reserved = isReservedRow(q);
                return (
                  <tr
                    key={q.id}
                    data-testid={`queue-row-${q.id}`}
                    className={reserved ? "bg-red-50/60 border-l-4 border-l-red-500" : ""}
                  >
                    <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                      <div className={reserved ? "font-bold text-red-700" : ""}>{timeForQueue(q)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">
                        {reserved ? "Reservation" : "Walk-in"}
                      </div>
                    </td>

                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-slate-900">{patientNameForQueue(q)}</div>
                        {reserved ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">
                            <CalendarClock size={10} />
                            Reserved
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                            Walk-in
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-slate-500">
                        {patientMetaForQueue(q)}
                      </div>

                      {(q.reason || q.reservation_time) && (
                        <div className={`text-xs mt-0.5 max-w-[260px] truncate ${reserved ? "text-red-700 font-medium" : "text-slate-600 italic"}`} title={q.reason}>
                          {reserved ? `Reserved slot ${q.reservation_time || ""}` : q.reason}
                          {reserved && q.reason ? ` · ${q.reason}` : ""}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-2">
                      {reserved ? (
                        <span className="text-xs text-slate-500 italic">
                          {q.assigned_doctor_id ? "Assigned" : "Unassigned"}
                        </span>
                      ) : (
                        <select
                          className="border border-slate-200 rounded-md px-2 py-1 text-sm bg-white"
                          value={q.assigned_doctor_id || ""}
                          onChange={(e) => reassign(q.id, e.target.value)}
                          data-testid={`reassign-${q.id}`}
                        >
                          {doctors.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                      )}
                    </td>

                    <td className="px-4 py-2 max-w-[260px]">
                      {reserved ? (
                        <span className="text-xs text-slate-400 italic">Check in first</span>
                      ) : hasAnyVital(q.vital_signs) ? (
                        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 leading-tight">
                          {vitalsSummary(q.vital_signs)}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Not recorded</span>
                      )}
                    </td>

                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs ${reserved ? STATUS_STYLE.reserved : STATUS_STYLE[q.status]}`}>
                        {reserved ? "Reserved" : (STATUS_LABEL[q.status] || q.status)}
                      </span>
                    </td>

                    <td className="px-4 py-2">
                      {reserved ? (
                        <div className="flex gap-1 flex-wrap">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => q.source === "follow_up" ? updateStatus(q.id, "waiting") : arriveReservationFromQueue(q)}
                            className="text-emerald-700"
                            data-testid={`btn-arrive-reservation-${q.id}`}
                          >
                            <CheckCircle2 size={12} className="mr-1" />
                            Arrived / Check In
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/patients/${q.patient_id}`)} data-testid={`btn-chart-${q.id}`}>
                            <Stethoscope size={12} className="mr-1" />
                            Open Chart
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => createRxFromQueue(q)} data-testid={`btn-rx-${q.id}`}>
                            <FilePlus2 size={12} className="mr-1 text-blue-600" /> Create RX
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => createPosFromQueue(q)} data-testid={`btn-pos-${q.id}`}>
                            <Receipt size={12} className="mr-1 text-emerald-600" /> Create POS
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openChartFromQueue(q)} data-testid={`btn-chart-${q.id}`}>
                            <Stethoscope size={12} className="mr-1" /> Open Chart
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEditPatient(q)} data-testid={`btn-edit-patient-${q.id}`}>
                            <Pencil size={12} className="mr-1 text-blue-600" /> Edit Patient
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => announcePatient(q)} disabled={!!callingId} data-testid={`btn-call-${q.id}`}>
                            <BellRing size={12} className="mr-1" /> Call Patient
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openVitalsDialog(q)} data-testid={`btn-vitals-${q.id}`}>
                            <Activity size={12} className="mr-1 text-emerald-600" /> Vitals
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openHistoryDialog(q)} data-testid={`btn-vitals-history-${q.id}`}>
                            <HistoryIcon size={12} className="mr-1" /> History
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateStatus(q.id, "billing")} disabled={q.status === "billing"}>
                            <Receipt size={12} className="mr-1 text-purple-600" /> Send to Cashier
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateStatus(q.id, "done")} disabled={q.status === "done"}>
                            <CheckCircle2 size={12} className="mr-1" /> Done
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateStatus(q.id, "cancelled")} disabled={q.status === "cancelled"}>
                            <X size={12} className="mr-1" /> Cancel
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Vitals edit dialog */}
      <Dialog
        open={vitalsDialog.open}
        onOpenChange={(open) => !open && setVitalsDialog({ open: false, queue: null, value: { ...EMPTY_VITALS }, saving: false })}
      >
        <DialogContent className="max-w-2xl" data-testid="vitals-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity size={16} className="text-emerald-600" />
              Vital Signs — {vitalsDialog.queue?.patient_snapshot?.full_name || ""}
            </DialogTitle>
          </DialogHeader>
          <VitalsForm
            value={vitalsDialog.value}
            onChange={(v) => setVitalsDialog((s) => ({ ...s, value: v }))}
            idPrefix="edit-vitals"
          />
          {vitalsDialog.queue?.vital_signs?.recorded_by && (
            <div className="text-xs text-slate-500 mt-2">
              Last recorded by {vitalsDialog.queue.vital_signs.recorded_by} on {fmtDateTime(vitalsDialog.queue.vital_signs.recorded_at)}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setVitalsDialog({ open: false, queue: null, value: { ...EMPTY_VITALS }, saving: false })}
              disabled={vitalsDialog.saving}
            >
              Cancel
            </Button>
            <Button onClick={saveVitals} disabled={vitalsDialog.saving} data-testid="btn-save-vitals">
              {vitalsDialog.saving ? "Saving..." : "Save Vitals"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vitals history dialog */}
      <Dialog
        open={historyDialog.open}
        onOpenChange={(open) => !open && setHistoryDialog({ open: false, patient: null, items: [], loading: false })}
      >
        <DialogContent className="max-w-2xl" data-testid="vitals-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HistoryIcon size={16} />
              Vitals History — {historyDialog.patient?.name || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto space-y-2">
            {historyDialog.loading && <div className="text-sm text-slate-500">Loading...</div>}
            {!historyDialog.loading && historyDialog.items.length === 0 && (
              <div className="text-sm text-slate-500 italic">No vitals recorded yet.</div>
            )}
            {historyDialog.items.map((it) => (
              <div key={it.id} className="border rounded p-3 bg-slate-50">
                <div className="flex justify-between items-start gap-2">
                  <div className="text-xs text-slate-500">
                    {fmtDateTime(it.date)} {it.visit_date ? `• Visit ${it.visit_date}` : ""}
                  </div>
                  <div className="text-xs text-slate-500">by {it.created_by || "—"}</div>
                </div>
                <div className="mt-1 text-sm text-slate-800">
                  {vitalsSummary(it.vitals || {}) || it.title}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit patient details (from the queue) */}
      <Dialog open={editPatient.open} onOpenChange={(o) => setEditPatient((s) => ({ ...s, open: o }))}>
        <DialogContent data-testid="queue-edit-patient-dialog">
          <DialogHeader>
            <DialogTitle>Edit Patient Details</DialogTitle>
          </DialogHeader>
          {editPatient.form ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input value={editPatient.form.first_name} onChange={(e) => setEditPatient((s) => ({ ...s, form: { ...s.form, first_name: e.target.value } }))} data-testid="edit-patient-first" />
              </div>
              <div>
                <Label>Middle Name</Label>
                <Input value={editPatient.form.middle_name} onChange={(e) => setEditPatient((s) => ({ ...s, form: { ...s.form, middle_name: e.target.value } }))} placeholder="Optional" data-testid="edit-patient-middle" />
              </div>
              <div>
                <Label>Surname *</Label>
                <Input value={editPatient.form.last_name} onChange={(e) => setEditPatient((s) => ({ ...s, form: { ...s.form, last_name: e.target.value } }))} data-testid="edit-patient-last" />
              </div>
              <div>
                <Label>Gender</Label>
                <select value={editPatient.form.gender} onChange={(e) => setEditPatient((s) => ({ ...s, form: { ...s.form, gender: e.target.value } }))} className="w-full h-10 border border-slate-300 rounded-md px-2" data-testid="edit-patient-gender">
                  <option value="">—</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <Label>Birthday</Label>
                <Input type="date" value={editPatient.form.birthday} onChange={(e) => setEditPatient((s) => ({ ...s, form: { ...s.form, birthday: e.target.value } }))} data-testid="edit-patient-birthday" />
              </div>
              <div>
                <Label>Contact</Label>
                <Input value={editPatient.form.contact_number} onChange={(e) => setEditPatient((s) => ({ ...s, form: { ...s.form, contact_number: e.target.value } }))} data-testid="edit-patient-contact" />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input value={editPatient.form.address} onChange={(e) => setEditPatient((s) => ({ ...s, form: { ...s.form, address: e.target.value } }))} data-testid="edit-patient-address" />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPatient({ open: false, saving: false, pid: "", queueId: "", form: null })}>Cancel</Button>
            <Button onClick={saveEditPatient} disabled={editPatient.saving} className="bg-blue-700 hover:bg-blue-800" data-testid="edit-patient-save">
              {editPatient.saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={voiceOpen} onOpenChange={setVoiceOpen}>
        <DialogContent className="max-w-lg" data-testid="callout-voice-modal">
          <DialogHeader>
            <DialogTitle>Call-out Voice</DialogTitle>
          </DialogHeader>
          {/* Audition against a real patient waiting right now — the whole point
              is hearing which voice pronounces actual names correctly. */}
          <CallOutVoicePicker
            bare
            sampleName={
              (queue.find((q) => q.status === "waiting") || queue[0])
                ?.patient_snapshot?.full_name || ""
            }
          />
        </DialogContent>
      </Dialog>

          <Dialog open={showBookingQr} onOpenChange={setShowBookingQr}>
        <DialogContent className="max-w-md" data-testid="booking-qr-modal">
          <DialogHeader>
            <DialogTitle>Booking QR Code</DialogTitle>
          </DialogHeader>

          <div className="text-center space-y-4">
            <div ref={qrRef} className="mx-auto w-64 h-64 bg-white border rounded-2xl p-3 flex items-center justify-center">
              {user?.active_hospital_id ? (
                <QRCodeCanvas value={bookingUrl} size={232} level="M" />
              ) : (
                <div className="text-xs text-slate-400">No hospital selected</div>
              )}
            </div>
            <div>
              <div className="font-bold text-slate-900">Scan to Reserve Appointment Online</div>
              <div className="text-xs text-slate-500 mt-0.5">{hospitalName}</div>
              <div className="text-xs text-slate-400 break-all mt-1">{bookingUrl}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(bookingUrl); toast.success("Link copied"); }} data-testid="qr-copy">
                <Copy size={14} className="mr-1" /> Copy
              </Button>
              <Button variant="outline" onClick={downloadQr} data-testid="qr-download">
                <Download size={14} className="mr-1" /> Download
              </Button>
              <Button variant="outline" onClick={printQr} data-testid="qr-print">
                <Printer size={14} className="mr-1" /> Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Doctor note pop-up */}
      <Dialog open={!!popupNote} onOpenChange={(o) => { if (!o) setPopupNote(null); }}>
        <DialogContent className="max-w-md" data-testid="reception-note-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BellRing size={16} className="text-amber-600" /> Note from Doctor</DialogTitle>
          </DialogHeader>
          {popupNote ? (
            <div className="space-y-2">
              <div className="text-xs text-slate-500">
                {popupNote.from_doctor_name || "Doctor"}{popupNote.patient_name ? ` · ${popupNote.patient_name}` : ""}
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-slate-900 whitespace-pre-line">{popupNote.message}</div>
            </div>
          ) : null}
          <DialogFooter>
            <Button onClick={() => popupNote && ackNote(popupNote.id)} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-ack-note">Okay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reception inbox */}
      <Dialog open={inboxOpen} onOpenChange={setInboxOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reception-inbox">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Inbox size={16} /> Reception Inbox</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {notes.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">No notes yet.</div>
            ) : notes.map((n) => (
              <div key={n.id} className={`rounded-md border p-3 ${n.status === "acknowledged" ? "border-slate-200 bg-white" : "border-amber-200 bg-amber-50"}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs text-slate-500">
                    {n.from_doctor_name || "Doctor"}{n.patient_name ? ` · ${n.patient_name}` : ""} · {(n.created_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                  {n.status === "acknowledged" ? (
                    <span className="text-[11px] text-emerald-700">✓ Acknowledged{n.acknowledged_by ? ` by ${n.acknowledged_by}` : ""}</span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => ackNote(n.id)} data-testid={`ack-${n.id}`}>Acknowledge</Button>
                  )}
                </div>
                <div className="mt-1 text-sm text-slate-900 whitespace-pre-line">{n.message}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
